import { useMemo } from 'react'
import { Link } from '@inertiajs/react'
import { AdminTable, type Column } from './AdminTable'
import { Pagination } from './Pagination'
import { DeleteButton } from './DeleteButton'
import { LoadingBar } from './LoadingBar'
import { focusRing } from '@/components/ui/buttonStyles'
import { useAdminTab } from '@/hooks/useAdminTab'
import { cn } from '@/utils/cn'
import { fetchAdminUsers, deleteAdminUser } from '@/api/admin'
import type { AdminUser } from '@/types/admin'

interface UsersTabProps {
  active: boolean
}

export function UsersTab({ active }: UsersTabProps) {
  const { data, meta, loading, setPage, refresh } = useAdminTab(fetchAdminUsers, active)

  const columns: Column<AdminUser>[] = useMemo(
    () => [
      {
        key: 'email',
        label: 'Email',
        render: (u) => (
          <Link
            href={`/admin/users/${u.id}`}
            className={cn(
              'inline-flex min-h-11 items-center break-all rounded text-accent transition-colors duration-200 hover:text-heading',
              focusRing,
            )}
          >
            {u.email}
          </Link>
        ),
      },
      {
        key: 'role',
        label: 'Role',
        render: (u) => (
          <span
            className={`inline-block rounded-full px-2 py-0.5 text-xs ${
              u.role === 'admin'
                ? 'bg-accent/10 text-accent'
                : 'bg-surface-bg text-muted'
            }`}
          >
            {u.role}
          </span>
        ),
      },
      {
        key: 'verified',
        label: 'Verified',
        render: (u) => (
          <span className={u.confirmed ? 'text-chip-success-text' : 'text-muted'}>
            {u.confirmed ? 'Yes' : 'No'}
          </span>
        ),
      },
      {
        key: 'otp',
        label: '2FA',
        render: (u) => (
          <span className={u.otp_enabled ? 'text-chip-success-text' : 'text-muted'}>
            {u.otp_enabled ? 'On' : 'Off'}
          </span>
        ),
      },
      {
        key: 'languages',
        label: 'Languages',
        render: (u) => u.languages_count,
        className: 'text-right',
      },
      {
        key: 'words',
        label: 'Words',
        render: (u) => u.words_tracked_count,
        className: 'text-right',
      },
      {
        key: 'joined',
        label: 'Joined',
        render: (u) => new Date(u.created_at).toLocaleDateString(),
      },
      {
        key: 'actions',
        label: '',
        render: (u) => (
          <DeleteButton
            label={`Delete user ${u.email}`}
            onDelete={() => deleteAdminUser(u.id)}
            onSuccess={refresh}
          />
        ),
        className: 'w-10',
      },
    ],
    [refresh],
  )

  if (!active) return null

  return (
    <div>
      {loading && <div className="mb-4"><LoadingBar /></div>}
      <AdminTable columns={columns} data={data} keyFn={(u) => u.id} rowHref={(u) => `/admin/users/${u.id}`} />
      <Pagination meta={meta} onPageChange={setPage} />
    </div>
  )
}
